import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/lib/supabaseClient";
import {
  createDeletionRequest,
  DeletionRequestInput,
} from "@/lib/datasource/supabase";

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
};

export default function AccountSheet({ open, onOpenChange }: Props) {
  const { toast } = useToast();
  const { user, displayName, setDisplayName } = useAuth();

  if (!user) {
    return null;
  }

  const [tab, setTab] = useState<"profile" | "password" | "account">("profile");
  const [name, setName] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => {
    if (open) {
      setName(displayName);
      setCurrentPassword("");
      setNewPassword("");
      setTab("profile");
    }
  }, [open, displayName]);

  const handleReset = () => {
    setName(displayName);
    setCurrentPassword("");
    setNewPassword("");
  };

  const handleSave = async () => {
    try {
      if (tab === "profile") {
        const { error } = await supabase.auth.updateUser({
          data: { name: name.trim() },
        });
        if (error) throw error;
        setDisplayName(name.trim());
        toast.success("Profile updated");
        onOpenChange(false);
      } else if (tab === "password") {
        if (!newPassword.trim() || newPassword.length < 8) {
          toast.error("Password must be at least 8 characters");
          return;
        }
        const { error } = await supabase.auth.updateUser({
          password: newPassword,
        });
        if (error) throw error;
        setCurrentPassword("");
        setNewPassword("");
        toast.success("Password changed");
        onOpenChange(false);
      } else if (tab === "account") {
        onOpenChange(false);
      }
    } catch (e: any) {
      console.error("[AccountSheet] Save error:", e);
      toast.error(e?.message ?? "Something went wrong");
    }
  };

  const handleDeleteRequest = async () => {
    if (!user?.id) {
      toast.error("User information is missing. Cannot proceed.", "Error");
      return;
    }

    try {
      if (typeof createDeletionRequest !== "function") {
        throw new Error(
          "DataSource misconfigured: createDeletionRequest not a function",
        );
      }

      const payload: DeletionRequestInput = {
        userId: user.id,
      };

      const { error } = await createDeletionRequest(payload);

      if (error) {
        toast.error(error);
        return;
      }

      toast.success(
        "Please check your email to confirm the account deletion.",
        "Deletion Request Sent",
      );
    } catch (e: any) {
      console.error("[AccountSheet] Deletion request error:", e);
      toast.error(
        e?.message ?? "Failed to send deletion request. Please try again.",
        "Error",
      );
    } finally {
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "p-0 gap-0",
          "w-[min(100vw-2rem,44rem)] max-h-[80vh]",
          "flex flex-col overflow-hidden",
        )}
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <div>
            <DialogTitle>Profile & Settings</DialogTitle>
            <DialogDescription className="mt-1">
              Update your profile and password; view account details.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex flex-col min-h-0 flex-1">
          <div className="px-6 bg-white sticky top-0 z-10 border-b">
            <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
              <TabsList className="mt-3 grid grid-cols-3 w-full mb-3">
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="password">Password</TabsTrigger>
                <TabsTrigger value="account">Account</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="overflow-y-auto flex-1">
            <Tabs value={tab}>
              <TabsContent value="profile" className="px-6 py-5 space-y-5">
                <div className="grid gap-2">
                  <Label htmlFor="name">Display name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                  />
                  <p className="text-xs text-slate-500">
                    Saved to your Supabase account (user metadata).
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    value={user?.email || ""}
                    disabled
                    className="bg-slate-50 text-slate-600"
                  />
                  <p className="text-xs text-slate-500">
                    Email changes are not supported here.
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="password" className="px-6 py-5 space-y-5">
                <div className="grid gap-2">
                  <Label htmlFor="current">Current password</Label>
                  <Input
                    id="current"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="new">New password</Label>
                  <Input
                    id="new"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                  />
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                  <p className="text-sm text-amber-800">
                    Use at least 8 characters. You are currently signed in; we
                    update your Supabase auth password directly.
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="account" className="px-6 py-5 space-y-5">
                <div className="grid gap-2">
                  <Label>Subscription tier</Label>
                  <Input
                    value={String(user?.subscription ?? "free")}
                    readOnly
                  />
                  <p className="text-xs text-slate-500">
                    Managed by Hotel Foundry (Stripe integration coming).
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label>Support</Label>
                  <a
                    className="text-blue-600 underline"
                    href="mailto:support@hotelfoundry.app"
                  >
                    support@hotelfoundry.app
                  </a>
                </div>
                <div className="rounded-lg border border-red-200 p-3">
                  <p className="mb-2 text-sm font-medium text-red-700">
                    Delete profile
                  </p>
                  <p className="mb-3 text-xs text-red-700">
                    This won't immediately delete data. We'll open an email to
                    support with your user details.
                  </p>
                  <Button
                    variant="destructive"
                    type="button"
                    onClick={handleDeleteRequest}
                  >
                    Request deletion
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button
            className="bg-primary-blue"
            size="sm"
            type="button"
            onClick={handleSave}
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
